import {Injectable} from '@angular/core';
import {WmWoundImage} from '../../interfaces/wound-management/wm-wound-image';
import {SecureTable} from '../secure.table';
import {db} from '../db.service';
import {WmWoundImageFactory} from '../../factories/wound-management/wm-wound-image-factory';
import {WmWoundImagesAdapter} from '../../adapters/wm-wound-images.adapter';
import {ImagesService} from '../../services/core/images.service';


@Injectable({
  providedIn: 'root'
})
export class WmWoundImagesTable extends SecureTable<WmWoundImage> {

  /**
   *
   * @param imagesService
   */
  constructor(private imagesService: ImagesService) {
    super(db.wmWoundImages);
    this._tableSyncSettings = {
      error: false,
      syncMode: 'default',
      dependencies: [],
      // @ts-ignore
      adapter: new WmWoundImagesAdapter<WmWoundImage>({
        api: ['wound-management', 'wound-images'],
        fromObject: WmWoundImageFactory.fromObject,
        toRemote: WmWoundImageFactory.toRemote
      })
    };
  }

  /**
   *
   * @param wmWoundDocumentationEntryUuid
   */
  public getByWmWoundDocumentationEntryUuid(wmWoundDocumentationEntryUuid: string): Promise<WmWoundImage[]> {
    return this.table.where('wmWoundDocumentationEntryUuid')
      .equals(wmWoundDocumentationEntryUuid).reverse().sortBy('created');
  }

  /**
   *
   * @param wmWoundDocumentationEntryUuids
   */
  public getByWmWoundDocumentationEntryUuids(wmWoundDocumentationEntryUuids: string[]): Promise<WmWoundImage[]> {
    return this.table.where('wmWoundDocumentationEntryUuid')
      .anyOf(wmWoundDocumentationEntryUuids)
      .reverse()
      .sortBy('created');
  }

  /**
   *
   * @param wmWoundDocumentationEntryUuids
   * @param withFiles
   */
  public async removeByWmWoundDocumentationEntryUuids(wmWoundDocumentationEntryUuids: string[], withFiles: boolean = true): Promise<void> {
    if(withFiles) {
      const wmWoundImages =
        await this.getByWmWoundDocumentationEntryUuids(wmWoundDocumentationEntryUuids);
      await this.deleteWmWoundImageFiles(wmWoundImages);
    }

    await this.table.where('wmWoundDocumentationEntryUuid')
      .anyOf(wmWoundDocumentationEntryUuids)
      .delete();
  }

  /**
   *
   * @param force
   * @param withFiles
   */
  public override async clear(force: boolean = false, withFiles: boolean = true): Promise<void> {
    if(withFiles) {
      const wmWoundImages = await this.table.toArray();
      await this.deleteWmWoundImageFiles(wmWoundImages);
    }
    await super.clear(force);
  }

  /**
   *
   * @param wmWoundImageUuids
   * @param force
   * @param withFiles
   */
  public override async removeMany(wmWoundImageUuids: string[],
                                   force: boolean = false,
                                   withFiles: boolean = true): Promise<void> {
    // TODO: Handle force
    if(withFiles) {
      await this.deleteWmWoundImageFiles(await this.getItems(wmWoundImageUuids));
    }
    await super.removeMany(wmWoundImageUuids, force);
  }

  /**
   *
   * @param wmWoundImageUuid
   * @param force
   * @param withFile
   */
  public override async remove(wmWoundImageUuid: string,
                               force: boolean = false,
                               withFile: boolean = true): Promise<void> {
    if(withFile) {
      await this.deleteWmWoundImageFile(await this.get(wmWoundImageUuid));
    }
    await super.remove(wmWoundImageUuid, force);
  }

  /**
   *
   * @param wmWoundImage
   */
  public async deleteWmWoundImageFile(wmWoundImage: WmWoundImage): Promise<void> {
    await this.deleteWmWoundImageFiles([wmWoundImage]);
  }

  /**
   * Deletes the file form the wound image from the storage
   *
   * @param wmWoundImages
   */
  public async deleteWmWoundImageFiles(wmWoundImages: WmWoundImage[]): Promise<void> {
    for(const wmWoundImage of wmWoundImages) {
      await this._deleteWmWoundImageFile(wmWoundImage);
    }
  }

  /**
   *
   */
  public async deleteSyncedWmWoundImageFiles(): Promise<void> {
    const syncedWmWoundImages = await this.getSynced();
    await this.deleteWmWoundImageFiles(syncedWmWoundImages);
  }

  /**
   *
   * @param wmWoundImage
   * @private
   */
  private _deleteWmWoundImageFile(wmWoundImage: WmWoundImage): Promise<void> {
    return new Promise((resolve) => {
      this.imagesService.deleteImage(wmWoundImage.file)
        .then(() => {
          resolve()
        })
        .catch((_error) => {
          console.info('File not found: ' + wmWoundImage.file.filepath);
          resolve();
        });
    });
  }

}
